import { Flex, FormField, Input } from 'pcln-design-system'
import { useEffect, useRef, useState, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from 'react'
import type { FieldValues, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import styled from 'styled-components'

const InputField = styled(Input)`
  &::-ms-reveal,
  &::-ms-clear {
    display: none;
  }
`

type InputCodeFieldType = {
  register?: UseFormRegister<FieldValues>
  name: string
  setValue?: UseFormSetValue<FieldValues>
  handleSubmit: ((values: string) => void) | (() => void)
  hasError?: boolean
  isSubmitting?: boolean
}
export default function InputCodeField({
  register,
  name,
  setValue,
  handleSubmit,
  hasError = false,
  isSubmitting = false,
}: InputCodeFieldType) {
  const codeLength = 6
  const [values, setValues] = useState(Array(codeLength).fill(''))
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])

  useEffect(() => {
    if (register) {
      register(name, { required: true })
    }
  }, [register, name])

  const handleSubmitForm = useRef(handleSubmit)
  useEffect(() => {
    const code = values.join('')
    if (code.length === codeLength && !values.includes('')) {
      if (setValue) {
        setValue(name, code, { shouldValidate: true })
      }
      handleSubmitForm.current(code)
    }
  }, [values, name, setValue])

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const { value } = e.currentTarget
    const newValues = [...values]
    newValues[index] = value
    setValues(newValues)
    if (value && index < codeLength - 1) {
      inputsRef.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key !== 'Backspace') return

    if (isSubmitting) {
      e.preventDefault()
      return
    }

    if (values[index]) {
      e.preventDefault()
      const newValues = [...values]
      newValues[index] = ''
      setValues(newValues)
      return
    }

    if (index > 0) {
      e.preventDefault()
      const newValues = [...values]
      newValues[index - 1] = ''
      setValues(newValues)
      inputsRef.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault()
    const paste = e.clipboardData?.getData('Text')
    const pasteCleansed = paste.replace(/\s+/g, '')
    const chars = pasteCleansed.slice(0, codeLength - index).split('')
    const newValues = [...values]
    chars.forEach((char, i) => {
      newValues[index + i] = char
    })
    inputsRef.current[index + chars.length - 1]?.focus()
    setValues(newValues)
  }

  return (
    <Flex style={{ gap: '8px' }}>
      {values.map((value, index) => (
        <FormField key={`${name}-${index}`}>
          <InputField
            id={`${name}-${index}`}
            data-testid={`${name}-${index}`}
            maxLength={1}
            inputMode='numeric'
            pattern='[0-9]*'
            ref={(el: HTMLInputElement) => {
              inputsRef.current[index] = el
            }}
            color={hasError ? 'error' : undefined}
            value={value}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={(e) => handlePaste(e, index)}
            aria-describedby={`helper-text-${name}-${index}`}
            aria-label={`${name}-${index}`}
            aria-invalid={!!hasError}
          />
        </FormField>
      ))}
    </Flex>
  )
}
